import { FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';

import { AnalyticsEvent, track } from '@bonusly/analytics';

import { useLocalStorage } from '../../lib/storage/hooks/use-local-storage';
import { ACTIVE_SEASON, getActiveSeasonConfig } from './seasonal-config';
import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalThemeModal } from './seasonal-theme-modal';

interface GlobalSeasonalThemeModalStore {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
}

const useGlobalSeasonalThemeModal = create<GlobalSeasonalThemeModalStore>((set) => ({
	isOpen: false,
	onOpen: () => set({ isOpen: true }),
	onClose: () => set({ isOpen: false }),
}));

const modalSeenStorageKey = () => `${getActiveSeasonConfig().storageKey}:modal-seen`;

export const GlobalSeasonalThemeModal: FC = () => {
	const { isOpen, onClose } = useGlobalSeasonalThemeModal();
	const { hasSeasonalThemePermission, toggleTheme } = useSeasonalTheme();
	const [hasSeenModal, setHasSeenModal] = useLocalStorage<boolean>(modalSeenStorageKey(), false);
	const location = useLocation();

	const isHomepage = location.pathname === '/';

	useEffect(() => {
		if (hasSeasonalThemePermission && !hasSeenModal && isHomepage) {
			setHasSeenModal(true);
			track(AnalyticsEvent.SeasonalTheme.ModalAutoShown, { season: ACTIVE_SEASON });
			useGlobalSeasonalThemeModal.getState().onOpen();
		}
	}, [hasSeasonalThemePermission, hasSeenModal, isHomepage, setHasSeenModal]);

	if (!hasSeasonalThemePermission) {
		return null;
	}

	return <SeasonalThemeModal isOpen={isOpen} onClose={onClose} onEnable={toggleTheme} />;
};
